import type { ApiRequest, ApiRequestWith } from "@/api/types";
import type {
  BatchCosignBody,
  BatchCosignResponse,
  BatchRejectBody,
  BatchRejectResponse,
  CosignDocumentBody,
  GetAddendumCosignMetaResponse,
  GetCosignQueueResponse,
  GetCosignQueueSearchParams,
  GetNoteCosignMetaResponse,
  GetNoteHistoryResponse,
  GetRejectedNotesCountResponse,
  RejectDocumentBody,
} from "@/core/types/(api)/cosignature";
import { getGroupAuthType } from "@/utils/authToken";
import { GROUP_AUTH_HEADER_NAME } from "@/api/constants";
import { httpAuth } from "@/api/httpAuth";
import type { UUID } from "@/core/types/common";

type CosignatureRequestsTyped = {
  batch_cosign: ApiRequestWith<BatchCosignResponse, { body: BatchCosignBody }>;
  batch_reject: ApiRequestWith<BatchRejectResponse, { body: BatchRejectBody }>;
  cosign_document: ApiRequestWith<
    void,
    { body: CosignDocumentBody; urlParams: { documentId: string } }
  >;
  get_addendum_cosign_meta: ApiRequestWith<
    GetAddendumCosignMetaResponse,
    { urlParams: { documentId: string } }
  >;
  get_cosign_queue: ApiRequest<
    GetCosignQueueResponse,
    { searchParams?: GetCosignQueueSearchParams }
  >;
  get_note_cosign_meta: ApiRequestWith<
    GetNoteCosignMetaResponse,
    { urlParams: { documentId: string } }
  >;
  get_note_history: ApiRequestWith<GetNoteHistoryResponse, { urlParams: { noteId: UUID } }>;
  get_rejected_notes_count: ApiRequest<GetRejectedNotesCountResponse>;
  reject_document: ApiRequestWith<
    void,
    { body: RejectDocumentBody; urlParams: { documentId: string } }
  >;
};

export const cosignatureRequestsTyped: CosignatureRequestsTyped = {
  batch_cosign: function ({ body, context }) {
    return httpAuth
      .post("/v1/cosignature/batch_cosign", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        json: body,
      })
      .json<BatchCosignResponse>();
  },

  batch_reject: function ({ body, context }) {
    return httpAuth
      .post("/v1/cosignature/batch_reject", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        json: body,
      })
      .json<BatchRejectResponse>();
  },

  cosign_document: function ({ body, context, urlParams }) {
    return httpAuth
      .post(`/v1/cosignature/${urlParams.documentId}/cosign`, {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        json: body,
        // disable timeout for long-polling endpoint — needs more time than the default (10 s)
        timeout: false,
      })
      .json<void>();
  },

  get_addendum_cosign_meta: function ({ context, urlParams }) {
    return httpAuth
      .get(`/v1/cosignature/note_addendum/${urlParams.documentId}/meta`, {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<GetAddendumCosignMetaResponse>();
  },

  get_cosign_queue: function ({ context, searchParams, signal } = {}) {
    return httpAuth
      .get("/v1/cosignature/queue", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        searchParams,
        signal,
      })
      .json<GetCosignQueueResponse>();
  },

  get_note_cosign_meta: function ({ context, urlParams }) {
    return httpAuth
      .get(`/v1/cosignature/note/${urlParams.documentId}/meta`, {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<GetNoteCosignMetaResponse>();
  },

  get_note_history: function ({ context, signal, urlParams }) {
    return httpAuth
      .get(`/v1/cosignature/note/${urlParams.noteId}/history`, {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        signal,
      })
      .json<GetNoteHistoryResponse>();
  },

  get_rejected_notes_count: function ({ context } = {}) {
    return httpAuth
      .get("/v1/cosignature/rejected_count", {
        context: { showLoader: false, ...context },
      })
      .json<GetRejectedNotesCountResponse>();
  },

  reject_document: function ({ body, context, urlParams }) {
    return httpAuth
      .post(`/v1/cosignature/${urlParams.documentId}/reject`, {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        json: body,
        // disable timeout for long-polling endpoint — needs more time than the default (10 s)
        timeout: false,
      })
      .json<void>();
  },
};
